import { useEffect, useRef, useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization } from "../../api/types";
import { desktopApp } from "../../app/desktop";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { SaveInput } from "../../ui/SaveInput";

/** What the shop chooses for its folder (server: folderSettingsSchema). */
export interface FolderSettings {
  accent: string;
  tagline: string;
  website: string;
  orderUrl: string;
  note: string;
  zones: boolean;
  /** null: the default (actions and deals off). */
  hiddenCategories: string[] | null;
}

/** GET /device/folder */
export interface FolderOverview {
  settings: FolderSettings;
  /** The shop has its own print as its folder (ADR 0052); for now only those shops make a folder. */
  ownPrint?: boolean;
  logo: boolean;
  photo: boolean;
  categories: { id: string; name: string; shown: boolean }[];
}

type Picture = "logo" | "photo";
const MAX_PICTURE_BYTES = 6 * 1024 * 1024;
const ACCENTS = Object.keys(t.folder.accents);

interface Props {
  /** The code that opened Beheer: whoever may change the menu makes the folder. */
  authorization: Authorization;
}

/** The file as base64, without "data:…;base64,". */
function base64Of(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(typeof reader.result === "string" ? reader.result.replace(/^data:[^,]*,/, "") : "");
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error("read"));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Beheer › Menu › Folder (ADR 0050): "Folder maken" opens the folder of the shop in a window of its own, where the
 * browser saves it as a PDF for the printer. Below it what goes on the folder besides the menu: the colour, the line
 * under the name, the website, the link of the QR, an own action, the zones, the categories and two pictures.
 */
export function FolderTab({ authorization }: Props) {
  const text = t.folder;
  const { api, report, toast } = useTill();
  const [data, setData] = useState<FolderOverview | null>(null);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const files = { logo: useRef<HTMLInputElement>(null), photo: useRef<HTMLInputElement>(null) };
  const staffToken = authorization.token;

  useEffect(() => {
    let stopped = false;
    api
      .request<FolderOverview>("GET", "/device/folder", { staffToken })
      .then((answer) => {
        if (!stopped) setData(answer);
      })
      .catch(report);
    return () => {
      stopped = true;
    };
  }, [api, report, staffToken]);

  if (data === null) return <p className="note">{text.loading}</p>;
  const settings = data.settings;

  const save = async (change: Partial<FolderSettings>) => {
    try {
      setData(
        await api.request<FolderOverview>("PUT", "/device/folder", { staffToken, body: { ...settings, ...change } }),
      );
      setMessage("");
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) setMessage(error.message);
      else report(error);
    }
  };

  const make = async () => {
    if (busy) return;
    setBusy(true);
    // a browser opens the window at the tap (a window opened later is blocked); the Tafo app for Windows opens the
    // link in Chrome or Edge itself, where "Opslaan als PDF" knows the size of the folder
    const desktop = desktopApp() !== null;
    const opened = desktop ? null : window.open("", "_blank");
    try {
      const { token } = await api.request<{ token: string; expiresAt: string }>("POST", "/device/folder/link", {
        staffToken,
      });
      const url = new URL(`/folder/${token}`, window.location.origin).href;
      setLink(url);
      if (opened) {
        opened.opener = null;
        opened.location.href = url;
      } else if (desktop) window.open(url, "_blank");
    } catch (error) {
      opened?.close();
      report(error);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (kind: Picture, file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_PICTURE_BYTES) {
      toast(text.tooLarge, "bad");
      return;
    }
    const contentType = file.type === "image/png" ? "image/png" : "image/jpeg";
    try {
      const body = { contentType, data: await base64Of(file) };
      setData(await api.request<FolderOverview>("PUT", `/device/folder/images/${kind}`, { staffToken, body }));
    } catch (error) {
      report(error);
    }
  };

  const remove = async (kind: Picture) => {
    try {
      setData(await api.request<FolderOverview>("DELETE", `/device/folder/images/${kind}`, { staffToken }));
    } catch (error) {
      report(error);
    }
  };

  const toggleCategory = (id: string) => {
    const hidden = new Set(data.categories.filter((category) => !category.shown).map((category) => category.id));
    if (hidden.has(id)) hidden.delete(id);
    else hidden.add(id);
    void save({ hiddenCategories: [...hidden] });
  };

  const field = (
    key: "tagline" | "website" | "orderUrl" | "note",
    label: string,
    placeholder: string,
    max: number,
    note?: string,
  ) => (
    <label className="wide">
      {label}
      <SaveInput
        value={settings[key]}
        placeholder={placeholder}
        maxLength={max}
        onSave={(typed) => {
          const value = typed.trim();
          if (key === "orderUrl" && value !== "" && !/^https?:\/\/[^\s]+\.[^\s]+$/i.test(value)) {
            setMessage(text.orderUrlWrong);
            return false;
          }
          void save({ [key]: value });
          return true;
        }}
      />
      {note !== undefined && <span className="note">{note}</span>}
    </label>
  );

  const picture = (kind: Picture, label: string) => (
    <div className="fold-pic">
      <span>
        {label}: <b>{data[kind] ? text.pictureSet : text.pictureNone}</b>
      </span>
      <input
        ref={files[kind]}
        type="file"
        accept="image/png,image/jpeg"
        hidden
        aria-label={label}
        onChange={(event) => {
          void upload(kind, event.target.files?.[0]);
          event.target.value = "";
        }}
      />
      <button
        type="button"
        className="btn small"
        onClick={() => {
          files[kind].current?.click();
        }}
      >
        {data[kind] ? text.replace : text.choose}
      </button>
      {data[kind] && (
        <button type="button" className="btn small danger ghost" onClick={() => void remove(kind)}>
          {text.remove}
        </button>
      )}
    </div>
  );

  // for now only a shop with its own print makes a folder (Furkan, 25/10/2026: "simdilik uitschakelen")
  if (data.ownPrint !== true) {
    return (
      <div className="folder-admin">
        <div className="zbox">
          <p>{text.notAvailable}</p>
          <button type="button" className="btn primary fold-make" disabled>
            {text.make}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="folder-admin">
      <div className="zbox">
        <p>{text.ownPrint}</p>
        <button type="button" className="btn primary fold-make" disabled={busy} onClick={() => void make()}>
          {busy ? text.making : text.make}
        </button>
        {link !== null && (
          <p className="note">
            {text.opened}{" "}
            <a href={link} target="_blank" rel="noreferrer">
              {text.openLink}
            </a>
          </p>
        )}
        <p className="note">{text.fromElsewhere}</p>
      </div>

      <div className="zbox">
        <h3>{text.look}</h3>
        <div className="fold-accents" role="group" aria-label={text.accent}>
          <span>{text.accent}</span>
          {ACCENTS.map((accent) => (
            <button
              key={accent}
              type="button"
              className={`fold-swatch sw-${accent.slice(1)}`}
              aria-pressed={settings.accent === accent}
              aria-label={text.accents[accent]}
              title={text.accents[accent]}
              onClick={() => void save({ accent })}
            />
          ))}
          <label className="inl">
            {text.accentCustom}
            <input
              type="color"
              value={settings.accent}
              onChange={(event) => void save({ accent: event.target.value })}
            />
          </label>
        </div>
        <div className="sfrm">
          {field("tagline", text.tagline, text.taglinePlaceholder, 60, text.taglineNote)}
          {field("website", text.website, text.websitePlaceholder, 80)}
          {field("orderUrl", text.orderUrl, text.orderUrlPlaceholder, 300, text.orderUrlNote)}
          {field("note", text.note, text.notePlaceholder, 80, text.noteNote)}
        </div>
        <p className="pinmsg left" role="alert">
          {message}
        </p>
        <label className="fold-check">
          <input
            type="checkbox"
            checked={settings.zones}
            onChange={(event) => void save({ zones: event.target.checked })}
          />
          {text.zones}
        </label>
      </div>

      <div className="zbox">
        <h3>{text.categories}</h3>
        <p className="note">{text.categoriesNote}</p>
        <div className="chips">
          {data.categories.map((category) => (
            <button
              key={category.id}
              type="button"
              className="chip"
              aria-pressed={category.shown}
              onClick={() => {
                toggleCategory(category.id);
              }}
            >
              {category.name}
              {!category.shown && <small> {text.off}</small>}
            </button>
          ))}
        </div>
      </div>

      <div className="zbox">
        <h3>{text.pictures}</h3>
        {picture("logo", text.logo)}
        {picture("photo", text.photo)}
        <p className="note">{text.pictureNote}</p>
      </div>
    </div>
  );
}
